import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  TextField,
  Autocomplete,
  Button,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Treatment type color mapping
const TYPE_COLORS = {
  'WELLNESS': { bg: '#d1fae5', text: '#059669', border: '#6ee7b7' },
  'PHYSIOTHERAPY': { bg: '#dbeafe', text: '#2563eb', border: '#93c5fd' },
  'DOCTOR CONSULTATION': { bg: '#e9d5ff', text: '#9333ea', border: '#c4b5fd' },
  'THERAPY': { bg: '#ccfbf1', text: '#0d9488', border: '#5eead4' },
  'AYURVEDA': { bg: '#fed7aa', text: '#ea580c', border: '#fdba74' },
  'FITNESS': { bg: '#fecaca', text: '#dc2626', border: '#fca5a5' },
  'NUTRITION': { bg: '#fef3c7', text: '#d97706', border: '#fde68a' },
  'DEFAULT': { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' }
};

const getTypeColor = (type) => {
  if (!type) return TYPE_COLORS.DEFAULT;
  const upperType = type.toUpperCase();
  return TYPE_COLORS[upperType] || TYPE_COLORS.DEFAULT;
};

// Format time
const formatTime = (time) => {
  if (time === null || time === undefined) return 'Pending';
  if (typeof time === 'string') return time;
  if (typeof time === 'number' && !isNaN(time)) {
    const hours = Math.floor(time / 60);
    const mins = time % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  }
  return 'Pending';
};

const PackageSchedulingTableView = ({
  schedule,
  bookingResults,
  editingTreatment,
  setEditingTreatment,
  availableTreatments,
  handleTreatmentChange,
  handleDurationChange,
  handleConsultationDateChange,
  getConsultationDateBounds,
  getPackageStartDateBounds,
  handleShiftPackageStartDate,
  startDate,
  isConsultationTreatment,
  isScheduleDateEditableTreatment,
  getScheduleDateEditLabel,
  getScheduleDateEditHelperText,
  saveTreatmentEdit,
  cancelTreatmentEdit,
  handleRetryBooking,
  handleDeleteTreatment,
  handleAddTreatment,
  treatmentInstructions,
  handleTreatmentInstructionChange,
  setStandaloneTreatmentData,
  setShowStandaloneApprovalModal
}) => {
  const [orderBy, setOrderBy] = useState('day');
  const [order, setOrder] = useState('asc');

  // Flatten all treatments from all days into a single array
  const flattenedTreatments = useMemo(() => {
    if (!schedule || !schedule.schedule) return [];
    
    const treatments = [];
    schedule.schedule.forEach((day, dayIndex) => {
      day.treatments.forEach((treatment, treatmentIndex) => {
        // Check if this treatment was booked
        const booking = bookingResults?.bookings?.find((b) => {
          if (b.date !== day.date) return false;
          if (treatment.inclusionId && b.inclusionId) return b.inclusionId === treatment.inclusionId;
          return b.treatmentName === treatment.name;
        });
        const failure = bookingResults?.failures?.find((f) => {
          if (f.date !== day.date) return false;
          if (treatment.inclusionId && f.inclusionId) return f.inclusionId === treatment.inclusionId;
          return f.treatmentName === treatment.name;
        });
        
        treatments.push({
          ...treatment,
          day: day.day,
          dayOfWeek: day.dayOfWeek,
          date: day.date,
          dayIndex,
          treatmentIndex,
          isBooked: !!booking,
          isFailed: !!failure,
          failureReason: failure?.reason || '',
          failureType: failure?.type || '',
          bookingId: booking?.id,
          failureReason: failure?.reason,
          instructionText: treatmentInstructions[treatment.inclusionId] || ''
        });
      });
    });
    
    return treatments;
  }, [schedule, bookingResults, treatmentInstructions]);

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort the treatments
  const sortedTreatments = useMemo(() => {
    const comparator = (a, b) => {
      let aValue, bValue;
      
      switch (orderBy) {
        case 'day':
          aValue = a.day;
          bValue = b.day;
          break;
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'treatment':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        case 'duration':
          aValue = a.duration || 0;
          bValue = b.duration || 0;
          break;
        case 'time':
          aValue = a.startTime || '';
          bValue = b.startTime || '';
          break;
        case 'status':
          aValue = a.isBooked ? 1 : 0;
          bValue = b.isBooked ? 1 : 0;
          break;
        default:
          aValue = a.day;
          bValue = b.day;
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    };
    
    return [...flattenedTreatments].sort(comparator);
  }, [flattenedTreatments, order, orderBy]);

  const isEditing = (treatment) => {
    return editingTreatment?.dayIndex === treatment.dayIndex && 
           editingTreatment?.treatmentIndex === treatment.treatmentIndex;
  };

  return (
    <Box>
      {startDate && handleShiftPackageStartDate && getPackageStartDateBounds && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: '1px solid #fde68a',
            borderRadius: 1,
            bgcolor: '#fffbeb',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          <TextField
            size="small"
            type="date"
            label="Package start date"
            value={startDate}
            onChange={(e) => handleShiftPackageStartDate(e.target.value)}
            inputProps={{
              min: getPackageStartDateBounds().minDate,
              max: getPackageStartDateBounds().maxDate,
            }}
            InputLabelProps={{ shrink: true }}
            helperText="Any day from check-in through checkout"
            sx={{ minWidth: 220 }}
          />
        </Box>
      )}
    <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 400px)' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'day'}
                direction={orderBy === 'day' ? order : 'asc'}
                onClick={() => handleRequestSort('day')}
              >
                Day
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'date'}
                direction={orderBy === 'date' ? order : 'asc'}
                onClick={() => handleRequestSort('date')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'treatment'}
                direction={orderBy === 'treatment' ? order : 'asc'}
                onClick={() => handleRequestSort('treatment')}
              >
                Treatment
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'type'}
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={() => handleRequestSort('type')}
              >
                Type
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'duration'}
                direction={orderBy === 'duration' ? order : 'asc'}
                onClick={() => handleRequestSort('duration')}
              >
                Duration
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'time'}
                direction={orderBy === 'time' ? order : 'asc'}
                onClick={() => handleRequestSort('time')}
              >
                Time
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'status'}
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={() => handleRequestSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Instructions</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedTreatments.map((treatment, index) => {
            const editing = isEditing(treatment);
            const colors = getTypeColor(treatment.type);
            
            return (
              <TableRow 
                key={`${treatment.dayIndex}-${treatment.treatmentIndex}-${index}`}
                sx={{
                  backgroundColor: treatment.isBooked ? '#f0fdf4' : 
                                   editing ? '#fef3c7' : 'white',
                  '&:hover': {
                    backgroundColor: treatment.isBooked ? '#dcfce7' : 
                                     editing ? '#fde68a' : '#f9fafb'
                  }
                }}
              >
                {/* Day */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Day {treatment.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {treatment.dayOfWeek}
                  </Typography>
                </TableCell>
                
                {/* Date */}
                <TableCell>
                  {editing && isScheduleDateEditableTreatment?.(treatment, availableTreatments) ? (
                    <TextField
                      size="small"
                      type="date"
                      label={getScheduleDateEditLabel?.(treatment, availableTreatments) || 'Date'}
                      value={treatment.date}
                      onChange={(e) =>
                        handleConsultationDateChange(
                          treatment.dayIndex,
                          treatment.treatmentIndex,
                          e.target.value
                        )
                      }
                      inputProps={{
                        min: getConsultationDateBounds?.().minDate,
                        max: getConsultationDateBounds?.().maxDate,
                      }}
                      InputLabelProps={{ shrink: true }}
                      helperText={getScheduleDateEditHelperText?.(treatment, availableTreatments)}
                      FormHelperTextProps={{ sx: { maxWidth: 220 } }}
                      sx={{ width: 170 }}
                    />
                  ) : (
                    <Typography variant="body2">
                      {treatment.date}
                    </Typography>
                  )}
                </TableCell>
                
                {/* Treatment */}
                <TableCell>
                  {editing ? (
                    <Autocomplete
                      size="small"
                      options={availableTreatments.filter(t => {
                        const currentTier = treatment.tier_level;
                        if (!currentTier) return true;
                        return t.tier_level === currentTier;
                      })}
                      getOptionLabel={(opt) => `${opt.name} (${opt.duration} min)`}
                      value={availableTreatments.find(t => t.name === treatment.name) || null}
                      onChange={(e, newValue) => {
                        if (newValue) {
                          if (treatment.isManuallyAdded && !treatment.name) {
                            setStandaloneTreatmentData({
                              dayIndex: treatment.dayIndex,
                              treatmentIndex: treatment.treatmentIndex,
                              treatment: newValue,
                              date: treatment.date
                            });
                            setShowStandaloneApprovalModal(true);
                          } else {
                            handleTreatmentChange(treatment.dayIndex, treatment.treatmentIndex, {
                              name: newValue.name,
                              duration: newValue.duration,
                              prepTime: newValue.preparation_time || 10,
                              cleanupTime: newValue.cleanup_time || 15,
                              type: newValue.type,
                              tier_level: newValue.tier_level
                            });
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label={`Treatment ${treatment.tier_level ? `(Tier: ${treatment.tier_level})` : ''}`}
                          size="small" 
                        />
                      )}
                      sx={{ minWidth: 250 }}
                    />
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {treatment.name}
                      </Typography>
                      {treatment.tier_level && (
                        <Typography variant="caption" color="text.secondary">
                          Tier: {treatment.tier_level}
                        </Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                
                {/* Type */}
                <TableCell>
                  <Chip 
                    label={treatment.type || 'OTHER'} 
                    size="small"
                    sx={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      borderColor: colors.border,
                      border: '1px solid',
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                </TableCell>
                
                {/* Duration */}
                <TableCell>
                  {editing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={treatment.duration}
                      onChange={(e) => handleDurationChange(treatment.dayIndex, treatment.treatmentIndex, e.target.value)}
                      inputProps={{ min: 15, max: 240, step: 5 }}
                      sx={{ width: 80 }}
                    />
                  ) : (
                    <Typography variant="body2">
                      {treatment.duration} min
                    </Typography>
                  )}
                </TableCell>
                
                {/* Time */}
                <TableCell>
                  <Typography variant="body2">
                    {formatTime(treatment.startTime)}
                  </Typography>
                </TableCell>
                
                {/* Status */}
                <TableCell>
                  {treatment.isBooked ? (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Booked" 
                      size="small" 
                      color="success"
                      sx={{ fontSize: '0.7rem', height: '24px' }}
                    />
                  ) : treatment.isFailed ? (
                    <Tooltip 
                      title={treatment.failureReason || 'Booking failed'} 
                      arrow
                      placement="top"
                    >
                      <Chip 
                        icon={
                          treatment.failureType === 'arrival_day_skip' || 
                          treatment.failureType === 'departure_day_skip' 
                            ? <InfoOutlinedIcon /> 
                            : <ErrorOutlineIcon />
                        }
                        label={
                          treatment.failureType === 'arrival_day_skip' || 
                          treatment.failureType === 'departure_day_skip' 
                            ? 'Skipped' 
                            : 'Failed'
                        }
                        size="small" 
                        color={
                          treatment.failureType === 'arrival_day_skip' || 
                          treatment.failureType === 'departure_day_skip' 
                            ? 'warning' 
                            : 'error'
                        }
                        sx={{ fontSize: '0.7rem', height: '24px', cursor: 'help' }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip 
                      label="Pending" 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '24px' }}
                    />
                  )}
                </TableCell>
                
                {/* Instructions */}
                <TableCell>
                  {!editing && !treatment.isBooked && (
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Instructions..."
                      value={treatment.instructionText}
                      onChange={(e) => handleTreatmentInstructionChange(treatment.inclusionId, e.target.value)}
                      sx={{ 
                        width: 150,
                        '& .MuiOutlinedInput-root': { 
                          fontSize: '0.75rem'
                        } 
                      }}
                    />
                  )}
                  {treatment.instructionText && treatment.isBooked && (
                    <Typography variant="caption" color="text.secondary">
                      {treatment.instructionText}
                    </Typography>
                  )}
                </TableCell>
                
                {/* Actions */}
                <TableCell align="center">
                  {editing ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button 
                        size="small" 
                        variant="contained"
                        onClick={() => saveTreatmentEdit(treatment.dayIndex, treatment.treatmentIndex)}
                        sx={{ 
                          minWidth: 60,
                          backgroundColor: '#E4C984', 
                          color: '#171717',
                          '&:hover': { backgroundColor: '#cbb16f' }
                        }}
                      >
                        Save
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={() => cancelTreatmentEdit()}
                        sx={{ minWidth: 60 }}
                      >
                        Cancel
                      </Button>
                      {!treatment.isBooked && treatment.name && handleRetryBooking && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            handleRetryBooking(
                              treatment.dayIndex,
                              treatment.treatmentIndex,
                              treatment,
                              treatment.date
                            )
                          }
                          sx={{
                            minWidth: 70,
                            backgroundColor: '#E4C984',
                            color: '#171717',
                            '&:hover': { backgroundColor: '#cbb16f' },
                          }}
                        >
                          Retry
                        </Button>
                      )}
                      {treatment.isManuallyAdded && (
                        <Button 
                          size="small" 
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteTreatment(treatment.dayIndex, treatment.treatmentIndex)}
                          sx={{ minWidth: 60 }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => setEditingTreatment({ dayIndex: treatment.dayIndex, treatmentIndex: treatment.treatmentIndex })}
                      disabled={treatment.isBooked}
                      sx={{
                        color: '#E4C984',
                        '&:hover': { backgroundColor: 'rgba(228, 201, 132, 0.1)' }
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      {sortedTreatments.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">No treatments scheduled</Typography>
        </Box>
      )}
    </TableContainer>
    </Box>
  );
};

export default PackageSchedulingTableView;

